<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-aria/focus';
import {HeaderInfo, PropTable} from '@react-spectrum/docs';
import packageData from '../package.json';

```tsx import
import {FocusRing} from '@react-aria/focus';
```

---
category: Focus
---

# FocusRing

<p>{docs.exports.FocusRing.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['FocusRing']} />

## Introduction

`FocusRing` is a utility component that can be used to apply a CSS class when an element has keyboard focus.
This helps keyboard users determine which element on a page or in an application has keyboard focus as they
navigate around. Focus rings are only visible when interacting with a keyboard so as not to distract mouse
and touch screen users.

If CSS classes are not being used for styling, see [useFocusRing](useFocusRing.html) for a hooks version.

## Props

<PropTable links={docs.links} component={docs.exports.FocusRing} />

## Example

This example shows how to use `<FocusRing>` to apply a CSS class when keyboard focus is on a button.

```css
.button {
  -webkit-appearance: none;
  appearance: none;
  background: green;
  border: none;
  font-size: 14px;
  padding: 4px 8px;
}

.button.focus-ring {
  outline: 2px solid dodgerblue;
  outline-offset: 2px;
}
```

```tsx example
<FocusRing focusRingClass="focus-ring">
  <button className="button">Test</button>
</FocusRing>
```
